var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useRef } from 'react';
import { playHowlAudio } from '../helper/helper';
import { MathJaxWrapper } from './MathJax';
import ToolTip from './ToolTip';
import '../styles/button.scss';
import globalStore from '../store';
import { t } from 'i18next';
import { getImageSrc } from '../helper/imageBlobCache';
// Reliable touch-primary detection — works on modern iPads (desktop UA) unlike browserDetect
export var IS_COARSE_POINTER = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
var ButtonComponent = function (props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    var _m = props.text, text = _m === void 0 ? '' : _m, _o = props.defaultCSS, defaultCSS = _o === void 0 ? '' : _o, _p = props.normalCSS, normalCSS = _p === void 0 ? '' : _p, _q = props.clickHandler, clickHandler = _q === void 0 ? function () { } : _q, _r = props.disabled, disabled = _r === void 0 ? false : _r, _s = props.avoidOnClickHoverReset, avoidOnClickHoverReset = _s === void 0 ? false : _s, _t = props.avoidDefaultStyle, avoidDefaultStyle = _t === void 0 ? false : _t, _u = props.avoidDisableClass, avoidDisableClass = _u === void 0 ? false : _u, _v = props.clickActive, clickActive = _v === void 0 ? false : _v, _w = props.showToolTip, showToolTip = _w === void 0 ? false : _w, _x = props.direction, direction = _x === void 0 ? 'right' : _x, toolTipCSS = props.toolTipCSS, _y = props.isParentRelative, isParentRelative = _y === void 0 ? false : _y, _z = props.isWhiteBoard, isWhiteBoard = _z === void 0 ? false : _z, _0 = props.isActive, isActive = _0 === void 0 ? false : _0, _1 = props.controlActiveFromOutside, controlActiveFromOutside = _1 === void 0 ? false : _1, _2 = props.useActiveImageWhenDisabled, useActiveImageWhenDisabled = _2 === void 0 ? false : _2, _3 = props.avoidDark, avoidDark = _3 === void 0 ? false : _3, _4 = props.type, type = _4 === void 0 ? '' : _4;
    var _5 = useState(false), buttonHovered = _5[0], setButtonHovered = _5[1];
    var _6 = useState(isActive), buttonActive = _6[0], setButtonActive = _6[1];
    // When controlActiveFromOutside=true the parent fully controls the active state.
    // Use isActive directly so the visual updates in the SAME render as the prop change
    // instead of one render later (the useState+useEffect lag). This fixes the "unlock"
    // flash on the main lock button when re-selecting a locked table cell.
    var effectiveActive = controlActiveFromOutside ? isActive : buttonActive;
    var btnRef = useRef(null);
    useEffect(function () {
        setButtonActive(isActive);
    }, [isActive]);
    // Blur the button when it transitions to a disabled state so the orange
    // focus ring doesn't linger on a control the user can no longer activate.
    // Combined with the tabIndex=-1 logic below this means disabled buttons
    // reliably leave the keyboard focus chain.
    useEffect(function () {
        if (disabled && !avoidDisableClass) {
            var el = btnRef.current;
            if (el && document.activeElement === el) {
                el.blur();
            }
        }
    }, [disabled, avoidDisableClass]);
    var contrast = globalStore.useStoreState(function (state) { return state.playerStore; }).contrast;
    useEffect(function () {
        var clickHandler = function (event) {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault(); // Prevent default action for space
                button === null || button === void 0 ? void 0 : button.dispatchEvent(new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    ctrlKey: event.ctrlKey,
                    metaKey: event.metaKey,
                    shiftKey: event.shiftKey,
                }));
            }
        };
        var button = btnRef.current;
        button === null || button === void 0 ? void 0 : button.addEventListener('keydown', clickHandler);
        return function () {
            button === null || button === void 0 ? void 0 : button.removeEventListener('keydown', clickHandler);
        };
    }, [btnRef]);
    // Cleanup effect to ensure button and tooltip are properly disposed
    useEffect(function () {
        return function () {
            // Clear button state on unmount
            setButtonHovered(false);
            setButtonActive(false);
        };
    }, []);
    var ariaHidden = props.ariaHidden
        ? props.ariaHidden
        : defaultCSS.indexOf('disableButton') !== -1
            ? avoidDisableClass
                ? false
                : true
            : false;
    // const cssNameSuffix =
    //   buttonHovered && buttonActive && clickActive && !props.rollOverDisabled
    //     ? '_activeHover'
    //     : buttonHovered && !props.rollOverDisabled
    //     ? '_hover'
    //     : buttonActive && clickActive
    //     ? '_active'
    //     : disabled && !clickActive
    //     ? '_disable'
    //     : disabled && buttonActive && clickActive
    //     ? '_activeDisable'
    //     : '';
    // const cssClass = `${defaultCSS} ${normalCSS} ${
    //   buttonHovered && !props.rollOverDisabled && `${normalCSS}_hover `
    // } ${buttonActive && clickActive && `${normalCSS}_active `} ${
    //   buttonHovered &&
    //   buttonActive &&
    //   clickActive &&
    //   !props.rollOverDisabled &&
    //   `${normalCSS}_activeHover `
    // }  ${disabled && !clickActive && `${normalCSS}_disable`} ${
    //   disabled && buttonActive && clickActive && `${normalCSS}_activeDisable`
    // } ${!avoidDefaultStyle && (buttonHovered ? 'skinBtnColors_hover' : 'skinBtnColors')} ${
    //   props.buttonStyleType ? `btnStyle_${props.buttonStyleType}${cssNameSuffix}` : ''
    // }`;
    var mainURL = disabled
        ? useActiveImageWhenDisabled && effectiveActive && clickActive
            ? ((_a = props.imgSrc) === null || _a === void 0 ? void 0 : _a["activeDisable".concat(contrast === 'normal' ? '' : 'dark')]) ||
                ((_b = props.imgSrc) === null || _b === void 0 ? void 0 : _b.active) ||
                ((_c = props.imgSrc) === null || _c === void 0 ? void 0 : _c["disable".concat(contrast === 'normal' ? '' : 'dark')]) ||
                ((_d = props.imgSrc) === null || _d === void 0 ? void 0 : _d["normal".concat(contrast === 'normal' ? '' : 'dark')])
            : ((_e = props.imgSrc) === null || _e === void 0 ? void 0 : _e["disable".concat(contrast === 'normal' ? '' : 'dark')]) ||
                ((_f = props.imgSrc) === null || _f === void 0 ? void 0 : _f["normal".concat(contrast === 'normal' ? '' : 'dark')])
        : buttonHovered
            ? effectiveActive && clickActive
                ? (_g = props.imgSrc) === null || _g === void 0 ? void 0 : _g["activeHover".concat(contrast === 'normal' ? '' : 'dark')]
                : (_h = props.imgSrc) === null || _h === void 0 ? void 0 : _h["hover".concat(contrast === 'normal' ? '' : 'dark')]
            : effectiveActive && clickActive
                ? (_j = props.imgSrc) === null || _j === void 0 ? void 0 : _j.active
                : (_k = props.imgSrc) === null || _k === void 0 ? void 0 : _k["normal".concat(contrast === 'normal' ? '' : 'dark')];
    return (_jsxs("button", { "data-position": props.position ? props.position : null, "data-value": props.value ? props.value : new Date().getTime(), style: __assign(__assign({ visibility: props.visibility ? 'hidden' : 'visible' }, (disabled && showToolTip ? { pointerEvents: 'auto', cursor: 'pointer' } : {})), props.buttonStyle), role: props.role ? props.role : undefined, ref: props.reference ? props.reference : btnRef, "aria-label": props.ariaLabelTranslated || (props.ariaLabel ? t(props.ariaLabel) : text), "data-id": props.dataId ? props.dataId : null, className: "".concat(type === '' ? '' : "".concat(type, "Btn"), " ").concat(!avoidDark && contrast === 'high' && 'dark', " ").concat(defaultCSS, " ").concat(normalCSS, " ").concat(buttonHovered &&
            !disabled &&
            !props.rollOverDisabled &&
            "".concat(type, "Btn_hover ").concat(normalCSS, "_hover "), " ").concat(effectiveActive && clickActive && "".concat(type, "Btn_active ").concat(normalCSS, "_active "), " ").concat(buttonHovered &&
            !disabled &&
            effectiveActive &&
            clickActive &&
            !props.rollOverDisabled &&
            "".concat(type, "Btn_activeHover ").concat(normalCSS, "_activeHover "), "  ").concat(disabled && !effectiveActive && "".concat(type, "Btn_disable ").concat(normalCSS, "_disable"), " ").concat(disabled && effectiveActive && "".concat(type, "Btn_activeDisable ").concat(normalCSS, "_activeDisable"), " ").concat(!avoidDefaultStyle && (buttonHovered && !disabled ? 'skinBtnColors_hover' : 'skinBtnColors'), "\n      ").concat(props.label ? 'buttonWithLabel' : '', "\n      "), "aria-hidden": ariaHidden ? true : false, tabIndex: props.ariaHidden
            ? -1
            : props.tabIndex !== undefined
                ? props.tabIndex
                : disabled && !avoidDisableClass
                    ? -1
                    : defaultCSS.indexOf('disableButton') !== -1
                        ? avoidDisableClass
                            ? 0
                            : -1
                        : 0, "aria-disabled": disabled || defaultCSS.indexOf('disableButton') !== -1 ? (ariaHidden ? false : true) : false, "aria-pressed": props.ariaPressed, "aria-checked": props.ariaChecked, "aria-haspopup": props.ariaHaspopup, "aria-expanded": props.ariaExpanded, "aria-controls": props.ariaControls, "aria-labelledby": props.ariaLabelledby, "aria-roledescription": props.ariaRoledescription, "aria-keyshortcuts": props.ariaKeyShortcuts, 
        // Native HTML `disabled` attribute — the only thing that guarantees a
        // button is excluded from Tab order AND auto-blurred when its disabled
        // state flips. We skip native disabled when `avoidDisableClass` is set
        // (some callers want the button to remain focusable for tooltip /
        // announcement purposes) or when `showToolTip` is true (tooltips need
        // pointer-events on a focusable element to work).
        disabled: disabled && !avoidDisableClass && !showToolTip ? true : undefined, onFocus: function (e) {
            var _a;
            if (!IS_COARSE_POINTER) {
                try {
                    if (e.target.matches(':focus-visible')) {
                        setButtonHovered(true);
                    }
                }
                catch (_b) {
                    // Safari/WebKit may throw SyntaxError for :focus-visible
                }
            }
            (_a = props.onFocus) === null || _a === void 0 ? void 0 : _a.call(props, e);
        }, onBlur: function (e) {
            var _a;
            setButtonHovered(false);
            (_a = props.onBlur) === null || _a === void 0 ? void 0 : _a.call(props, e);
        }, onClick: function (e) {
            if (disabled)
                return;
            if (e.target.classList.toString().indexOf('disableButton') !== -1) {
                return false;
            }
            if (!props.noAudio) {
                playHowlAudio({
                    id: 'click',
                });
            }
            !controlActiveFromOutside && setButtonActive(function (prev) { return !prev; });
            !avoidOnClickHoverReset && setButtonHovered(false);
            clickHandler && clickHandler(e);
        }, onMouseOver: function (e) {
            // Skip the hover-state class on coarse-pointer (touch) devices.
            // iOS synthesizes a mouseover when you tap a button but doesn't
            // reliably fire mouseout afterward, leaving the hover style stuck
            // (most visible on the linking-cubes right-panel buttons).
            // The onFocus path already follows this same guard.
            if (!IS_COARSE_POINTER) {
                setButtonHovered(true);
            }
            if (!disabled) {
                props.onMouseOver && props.onMouseOver(e);
            }
        }, onMouseOut: function (e) {
            setButtonHovered(false);
            if (!disabled) {
                props.onMouseOut && props.onMouseOut(e);
            }
        }, onKeyDown: props.onKeyDown
            ? function (e) {
                if (disabled)
                    return;
                props.onKeyDown(e);
            }
            : undefined, onKeyUp: props.onKeyUp
            ? function (e) {
                if (disabled)
                    return;
                props.onKeyUp(e);
            }
            : undefined, onMouseDown: props.onMouseDown, onTouchStart: props.onTouchStart, onTouchEnd: props.onTouchEnd, "aria-valuenow": props.ariaValueNow, "aria-valuemin": props.ariaValueMin, "aria-valuemax": props.ariaValueMax, "aria-valuetext": props.ariaValueText, onTouchCancel: props.onTouchCancel, children: [showToolTip && (_jsx(ToolTip, { buttonHovered: buttonHovered || !!props.forceHovered, text: props.title ? t(props.title) : '', direction: direction, toolTipCSS: toolTipCSS, isParentRelative: isParentRelative, isWhiteBoard: isWhiteBoard, 
                // `titleTranslated` rides in on `content`, not `text`, because ToolTip
                // renders `content ?? t(text)` — anything passed as `text` is run
                // through `t()` in there regardless of what we already did to it. An
                // explicit `toolTipContent` still wins; it is the more specific prop.
                content: (_l = props.toolTipContent) !== null && _l !== void 0 ? _l : props.titleTranslated, useViewportPosition: props.useViewportPosition })), (props === null || props === void 0 ? void 0 : props.label) && _jsx("label", { children: props === null || props === void 0 ? void 0 : props.label }), props.imgSrc ? (_jsx("span", { style: { pointerEvents: 'none', opacity: disabled ? 0.7 : 1 }, "aria-hidden": true, children: _jsx("img", { alt: "", src: getImageSrc(mainURL), style: { pointerEvents: 'none' }, "aria-hidden": true, width: props.imgWidth ? props.imgWidth : 'auto', height: props.imgHeight ? props.imgHeight : '100%' }) })) : (_jsx("span", { style: { pointerEvents: 'none' }, "aria-hidden": true, children: _jsx(MathJaxWrapper, { children: text }) })), (props === null || props === void 0 ? void 0 : props.children) && props.children] }));
};
export default ButtonComponent;
